import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade } from 'swiper/modules';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSite } from '@/lib/SiteContext';
import ProductImage from '@/components/ProductImage';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';

const bgGradients = [
  'from-brand-pink/30 to-brand-blue/20',
  'from-brand-blue/30 to-brand-pink/20',
  'from-brand-pink/20 to-brand-blue/30',
  'from-brand-blue/20 to-brand-pink/30',
];

export default function Hero() {
  const { heroSlides, settings, loading } = useSite();

  if (loading) {
    return (
      <section className="relative w-full h-[85vh] md:h-[90vh] bg-brand-cream flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-dark/20 border-t-brand-dark rounded-full animate-spin" />
      </section>
    );
  }

  const slides = heroSlides.length > 0 ? heroSlides : [];

  const FeatureList = ({ features }) => {
    const list = features && features.length > 0 
      ? features.slice(0, 5) // 5 maddeye kadar izin veriyoruz
      : ["Birinci Sınıf Hammadde", "Modern Tasarım", "Dayanıklı Yapı", "Ayz Plastik Güvencesi"];

    return (
      <ul className="flex flex-col items-center md:items-start gap-1.5 md:gap-2 mb-6 md:mb-8 px-4 md:px-0">
        {list.map((item, index) => (
          <motion.li
            key={index}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ 
              duration: 0.4, 
              delay: 0.5 + (index * 0.1),
              ease: "easeOut" 
            }}
            // FONT BOYUTU KÜÇÜLTÜLDÜ: text-[13px] md:text-[14px]
            className="flex items-start gap-2.5 text-[13px] md:text-[14px] text-brand-dark/70 font-medium leading-snug text-left"
          >
            <div className="mt-1 w-4 h-4 rounded-full bg-brand-blue/10 flex items-center justify-center shrink-0">
              <Check size={10} className="text-brand-blue" strokeWidth={3} />
            </div>
            <span>{item}</span>
          </motion.li>
        ))}
      </ul>
    );
  };

  return (
    <section data-testid="hero-section" className="relative w-full h-[100svh] min-h-[700px] md:h-[95vh] overflow-hidden">
      <Swiper
        modules={[Autoplay, Pagination, EffectFade]}
        effect="fade"
        autoplay={{ delay: 6000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        loop={slides.length > 1}
        className="w-full h-full"
      >
        {slides.map((product, i) => (
          <SwiperSlide key={product.id}>
            {/* ÜST BOŞLUK ARTIRILDI: pt-32 md:pt-40 -> Yazıları aşağı ittik */}
            <div className={`w-full h-full bg-gradient-to-br ${bgGradients[i % bgGradients.length]} bg-brand-cream flex items-center pt-32 pb-16 md:pt-20 md:pb-0`}>
              <div className="container mx-auto px-4 md:px-6 max-w-7xl h-full flex flex-col md:flex-row items-center justify-center gap-10 md:gap-16">
                
                {/* Sol Taraf: Metin Bloğu */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="flex-1 w-full text-center md:text-left z-10 flex flex-col items-center md:items-start order-2 md:order-1"
                >
                  <span className="inline-block px-3 py-1 rounded-full bg-white/40 backdrop-blur text-[10px] font-bold uppercase tracking-[0.2em] text-brand-dark/50 mb-4 md:mb-6">
                    {product.category || settings.companyName}
                  </span>
                  
                  <div className="flex flex-col justify-center w-full">
                    {/* BAŞLIK MARGIN AYARI: mb-4 md:mb-6 */}
                    <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-[4.5rem] font-black text-brand-dark tracking-tighter leading-[1.05] mb-5 md:mb-7 px-2 md:px-0 uppercase">
                      {product.name}
                    </h1>
                    
                    {/* Liste Alanı */}
                    <FeatureList features={product.features} />
                  </div>

                  {/* FİYAT ALANI */}
                  {product.price > 0 && (
                    <div className="mb-8 md:mb-10">
                      <span className="text-[10px] font-bold text-brand-dark/30 uppercase tracking-widest block mb-1">Başlayan Fiyatlarla</span>
                      <p className="text-3xl md:text-4xl font-black text-brand-dark tracking-tighter">
                        {Number(product.price).toLocaleString('tr-TR')} TL
                      </p>
                    </div>
                  )}
                  
                  <div className="flex flex-col sm:flex-row justify-center md:justify-start gap-3 md:gap-4 w-full sm:w-auto px-4 md:px-0">
                    <Link
                      to={`/urunler/${product.id}`}
                      className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full bg-brand-dark text-white text-sm font-bold hover:bg-brand-dark/90 transition-all duration-300 shadow-xl shadow-brand-dark/20 w-full sm:w-auto active:scale-95"
                    >
                      Ürünü İncele
                      <ArrowRight size={18} />
                    </Link>
                    <Link
                      to="/urunler"
                      className="inline-flex items-center justify-center gap-2 px-10 py-4 rounded-full border-2 border-brand-dark/20 text-brand-dark text-sm font-bold hover:border-brand-dark hover:bg-brand-dark hover:text-white transition-all duration-300 w-full sm:w-auto active:scale-95"
                    >
                      Tüm Ürünler
                    </Link>
                  </div>
                </motion.div>

                {/* Sağ Taraf: Görsel (Biraz daha küçültülerek denge sağlandı) */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1, delay: 0.4, ease: [0.76, 0, 0.24, 1] }}
                  className="flex-1 w-full max-w-[260px] sm:max-w-sm md:max-w-xl mx-auto flex items-center justify-center order-1 md:order-2"
                >
                  <div className="relative w-full aspect-square flex items-center justify-center">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[90%] bg-gradient-to-br from-brand-pink/30 to-brand-blue/30 rounded-full blur-[80px] md:blur-[120px] opacity-60 mix-blend-multiply pointer-events-none" />
                    
                    <div className="relative w-full h-full z-10 animate-float drop-shadow-[0_20px_50px_rgba(0,0,0,0.1)]">
                      <ProductImage
                        src={product.images?.[0]}
                        alt={product.name}
                        name={product.name}
                        className="w-full h-full object-contain mix-blend-darken transition-transform duration-700 hover:scale-105"
                      />
                    </div>
                  </div>
                </motion.div>

              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Dekoratif Keşfet Çizgisi */}
      <div className="hidden md:flex absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex-col items-center gap-3 pointer-events-none opacity-30">
        <span className="text-[9px] font-bold text-brand-dark uppercase tracking-[0.4em]">Scroll</span>
        <motion.div
          animate={{ height: [40, 0, 40], originY: [0, 0, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-[1px] h-10 bg-brand-dark"
        />
      </div>
    </section>
  );
}